import styles from "./ReportPage.module.css";
import React, {useEffect, useState} from "react";
import {Administrator} from "../../services/models/administrator";
import {isMinzdravUser, isRoivUser, reportUsersAllowed, SubUser} from "../../services/models/sub_user";
import {useFetchSubUsersByEmail} from "../../hooks/useQuery";
import {ReportHeader} from "../../components/Report/ReportHeader/ReportHeader";
import {getLocalWeek} from "../../Utils/translateDate";
import {LogsTable} from "../../components/Report/LogsTable/LogsTable";
import {convertFromBackendFormat, convertToBackendFormat, ReportData} from "../../types/report.types";
import {useUpsertReport} from "../../hooks/useQuery/useUpsertReport";
import {useFetchReports} from "../../hooks/useQuery/useFetchReports";
import {ReportType} from "../../services/models/report";
import {ReportTable} from "../../components/Report/ReportTable/ReportTable";
import {LogsChart} from "../../components/Report/LogsChart/LogsChart";

interface ReportPageProps {
    user: Administrator | SubUser | null;
}

function isAdmin(obj: any): obj is Administrator {
    return obj && obj.role === undefined;
}


export const ReportPage: React.FC<ReportPageProps> = ({user}) => {
    const userIsAdmin = isAdmin(user);
    const isRoivOrMinzdrav = user && (isRoivUser(user as SubUser) || isMinzdravUser(user as SubUser));
    const isAllowedUser = user && reportUsersAllowed.includes((user as SubUser).email);
    const {
        usersData,
        usersError,
        usersIsLoading
    } = useFetchSubUsersByEmail(reportUsersAllowed, userIsAdmin, isRoivUser(user as SubUser), isMinzdravUser(user as SubUser));

    const reportMutation = useUpsertReport();

    const [selectedWeek, setSelectedWeek] = useState<{ year: number; week: number }>({
        year: new Date().getFullYear(),
        week: getLocalWeek(new Date()),
    });

    const [reportData, setReportData] = useState<ReportData>({
        annualInspectionPlan: 0,
        resultPO: {},
        resultPOChildren: {}
    });

    const ownerId = !userIsAdmin && user ? (user as SubUser).id : null;

    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [reportType, setReportType] = useState<ReportType>(
        isRoivOrMinzdrav && !isAllowedUser ? "logsChart" : "report"
    );
    useEffect(() => {
        if (userIsAdmin && usersData && usersData.length > 0 && !selectedUserId) {
            const firstUser = usersData[0];
            if (firstUser && firstUser._id) {
                setSelectedUserId(firstUser._id);
            } else if (firstUser && firstUser.id) {
                setSelectedUserId(firstUser.id);
            }
        }
    }, [userIsAdmin, usersData, selectedUserId]);

    // Для разрешенных пользователей используем их собственный ID, для остальных - выбранного пользователя (если есть) или свой
    const ownerForRequest = isAllowedUser ? ownerId : (selectedUserId || ownerId);

    // Определяем, нужно ли загружать данные отчета (для тех, кто имеет доступ к форме отчета)
    const shouldFetchReports = Boolean(userIsAdmin || isAllowedUser || !isRoivOrMinzdrav);

    const {reportsData, reportsError, reportsIsLoading} = useFetchReports(
        selectedWeek.week,
        selectedWeek.year,
        ownerForRequest,
        shouldFetchReports
    )
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [dataError, setDataError] = useState<string | null>(null);

    const handleReportDataChange = (newData: ReportData) => {
        setReportData(newData);
    };

    useEffect(() => {
        if (reportsData) {
            const uiData = convertFromBackendFormat(reportsData);
            setReportData(uiData);
        } else {
            setReportData({
                annualInspectionPlan: 0,
                resultPO: {},
                resultPOChildren: {}
            });
        }
    }, [reportsData]);

    useEffect(() => {
        if (reportsIsLoading) {
            setReportData({
                annualInspectionPlan: 0,
                resultPO: {},
                resultPOChildren: {}
            });
        }
    }, [selectedWeek, reportsIsLoading]);

    const isLoading = usersIsLoading || isDataLoading || reportsIsLoading;
    const isError = Boolean(usersError) || Boolean(dataError) || Boolean(reportsError);

    const handleSaveReport = async () => {
        try {
            setIsDataLoading(true);
            setDataError(null);
            const backendData = convertToBackendFormat(reportData, selectedWeek.week, selectedWeek.year);
            backendData.owner = ownerForRequest
            await reportMutation.mutateAsync(backendData);
        } catch (error) {
            console.error('Ошибка при сохранении данных:', error);
        } finally {
            setIsDataLoading(false);
        }

    };

    const handleClearField = async (section: 'resultPO' | 'resultPOChildren', key: string, fieldLabel: string) => {
        try {
            setIsDataLoading(true);
            setDataError(null);
            
            // Создаем объект для отправки с null значением для очищаемого поля
            const backendData: any = {
                reportingWeek: selectedWeek.week,
                reportingYear: selectedWeek.year,
                owner: ownerForRequest
            };

            // Добавляем только очищаемое поле с null значением
            if (section === 'resultPO') {
                backendData.resultPO = {
                    [key]: null
                };
            } else {
                backendData.resultPOChildren = {
                    [key]: null
                };
            }

            await reportMutation.mutateAsync(backendData);
            
            // Обновляем локальное состояние
            const newData = {...reportData};
            if (section === 'resultPO') {
                newData.resultPO[key] = {weekly: null, total: newData.resultPO[key]?.total || null};
            } else {
                newData.resultPOChildren[key] = {
                    weekly: null, 
                    total: newData.resultPOChildren[key]?.total || null,
                    totalWithPrevYear: newData.resultPOChildren[key]?.totalWithPrevYear || null
                };
            }
            setReportData(newData);
        } catch (error) {
            console.error('Ошибка при очистке поля:', error);
        } finally {
            setIsDataLoading(false);
        }
    };

    return (
        <div className={styles.reportPage}>

            <ReportHeader
                isAdmin={userIsAdmin}
                users={usersData}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                onSave={handleSaveReport}
                isSaving={isDataLoading}
                selectedUserId={selectedUserId}
                onUserChange={setSelectedUserId}
                setReportType={setReportType}
                reportType={reportType}
                reportData={reportsData} // Добавляем данные отчета для экспорта
                userName={user ? (user as SubUser).full_name || (user as Administrator).full_name || 'Пользователь' : 'Пользователь'} // Добавляем имя пользователя
                user={user}
                isAllowedUser={isAllowedUser}
            />


            {!isLoading && !isError && reportType === "report" && (userIsAdmin || isAllowedUser) && (
                <ReportTable
                    isAdmin={userIsAdmin}
                    reportData={reportData}
                    onDataChange={handleReportDataChange}
                    onClearField={handleClearField}
                    isLoading={isLoading}
                    isError={isError}
                    selectedYear={selectedWeek.year}
                    selectedWeek={selectedWeek.week}
                />
            )}
            {(reportType === "logs" && userIsAdmin) && (
                <LogsTable
                    isAdmin={userIsAdmin}
                    reportData={reportData}
                    onDataChange={handleReportDataChange}
                />
            )}
            {(reportType === "logsChart" && (userIsAdmin || isRoivOrMinzdrav || isAllowedUser)) && (
                <LogsChart
                    users={usersData}
                    onUserChange={setSelectedUserId}
                    annualInspectionPlan={reportsData?.annualInspectionPlan || 0}
                    currentUser={user as SubUser}
                />
            )}
        </div>
    );
};
